import { useState } from "react";
import Swal from "sweetalert2";
import {
  validateLinkImage,
  compressLinkImage,
  uploadLinkImage,
  linkImageErrorMessage,
} from "../utils/linkImage";
import { withHttps } from "../utils/links";

// State and handlers for the editable list of links: text edits, add/remove, drag reorder and image-link uploads.
const useLinkFields = (t) => {
  const [inputFields, setInputFields] = useState([
    { social_name: "", image: "", link: "" },
  ]);
  const [dragIndex, setDragIndex] = useState(null);
  const [linkCrop, setLinkCrop] = useState(null); // { uid, src } while cropping an image link

  const handleFormChange = (index, event) => {
    const { name, value } = event.target;
    setInputFields((fields) =>
      fields.map((field, i) => (i === index ? { ...field, [name]: value } : field))
    );
  };
  const handleLinkBlur = (index) => {
    setInputFields((fields) =>
      fields.map((field, i) => (i === index ? { ...field, link: withHttps(field) } : field))
    );
  };
  const remove = (index) => {
    setInputFields((fields) => fields.filter((_, i) => i !== index));
  };

  const handleDragStart = (event, index) => {
    setDragIndex(index);
    event.dataTransfer.effectAllowed = "move";
  };
  const handleDragEnd = () => setDragIndex(null);
  const handleDrop = (event, index) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    setInputFields((fields) => {
      const next = [...fields];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(null);
  };

  const addFields = () => {
    let newfield = { social_name: "", image: "", link: "" };

    setInputFields([...inputFields, newfield]);
  };
  const addImageField = () => {
    setInputFields([
      ...inputFields,
      {
        type: "image",
        social_name: "",
        link: "",
        image_path: "",
        image_url: "",
        uploading: false,
        uid: Math.random().toString(36).slice(2),
      },
    ]);
  };

  // Rows are found by uid, not index, because the row may be deleted while its upload is in flight.
  const patchImageField = (uid, patch) => {
    setInputFields((fields) =>
      fields.map((field) => (field.uid === uid ? { ...field, ...patch } : field))
    );
  };
  // Picking a file opens the cropper; the cropped, compressed result is what gets uploaded.
  const handleLinkImagePick = (row, event) => {
    const file = event.target.files[0];
    event.target.value = ""; // allow re-picking the same file
    if (!file) return;
    const problem = validateLinkImage(file);
    if (problem) {
      Swal.fire({ icon: "error", title: t(problem), text: "" });
      return;
    }
    setLinkCrop({ uid: row.uid, src: URL.createObjectURL(file) });
  };
  const closeLinkCropper = () => {
    if (linkCrop) URL.revokeObjectURL(linkCrop.src);
    setLinkCrop(null);
  };
  const handleLinkImageCropped = async (cropped) => {
    const uid = linkCrop.uid;
    closeLinkCropper();
    patchImageField(uid, { uploading: true });
    try {
      const file = await compressLinkImage(cropped);
      const { data } = await uploadLinkImage(file);
      patchImageField(uid, {
        image_path: data.image_path,
        image_url: data.image_url,
        uploading: false,
      });
    } catch (err) {
      patchImageField(uid, { uploading: false });
      Swal.fire({ icon: "error", title: t(linkImageErrorMessage(err)), text: "" });
    }
  };

  return {
    inputFields,
    setInputFields,
    dragIndex,
    linkCrop,
    handleFormChange,
    handleLinkBlur,
    remove,
    handleDragStart,
    handleDragEnd,
    handleDrop,
    addFields,
    addImageField,
    handleLinkImagePick,
    closeLinkCropper,
    handleLinkImageCropped,
  };
};

export default useLinkFields;
